import React from 'react';
import { ArrowRight, Phone, Mail } from 'lucide-react';
import { Button } from './ui/button';

const Hero = () => {
  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="hero" className="relative pt-32 pb-20 lg:pt-40 lg:pb-28 overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#f5f3ed] to-white opacity-70"></div>

      <div className="container mx-auto px-4 lg:px-8 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <div className="space-y-8 animate-fadeInUp">
            <div className="inline-block">
              <span className="px-4 py-2 bg-[#2d5f3f]/10 text-[#2d5f3f] rounded-full text-sm font-semibold">
                Premium Horsebox Hire - North Yorkshire
              </span>
            </div>
            
            <h1 className="text-5xl lg:text-6xl xl:text-7xl font-bold text-gray-900 leading-tight">
              <span className="font-['Playfair_Display']">
                Your Horse's
              </span>
              <br />
              <span className="text-[#2d5f3f] font-['Playfair_Display']">
                Journey Matters
              </span>
            </h1>

            <p className="text-lg lg:text-xl text-gray-600 leading-relaxed max-w-xl">
              Premium self-drive horsebox hire with our state-of-the-art 3.5-ton 
              Renault Master, crafted by Foxy Horseboxes. As a friendly, helpful, family-run company 
              based in the heart of North Yorkshire, we provide a safe, comfortable, and reliable way for you to transport your horse in style.
            </p>

            {/* Key Benefits */}
            <div className="grid grid-cols-2 gap-4 max-w-xl">
              <div className="flex items-start space-x-3">
                <div className="w-2 h-2 rounded-full bg-[#2d5f3f] mt-2"></div>
                <div>
                  <p className="font-semibold text-gray-900">Newly Built</p>
                  <p className="text-sm text-gray-600">2025 Foxy Horsebox</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <div className="w-2 h-2 rounded-full bg-[#2d5f3f] mt-2"></div>
                <div>
                  <p className="font-semibold text-gray-900">Easy to Drive</p>
                  <p className="text-sm text-gray-600">3.5-ton Renault Master</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <div className="w-2 h-2 rounded-full bg-[#2d5f3f] mt-2"></div>
                <div>
                  <p className="font-semibold text-gray-900">From £100</p>
                  <p className="text-sm text-gray-600">Weekday rates</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <div className="w-2 h-2 rounded-full bg-[#2d5f3f] mt-2"></div>
                <div>
                  <p className="font-semibold text-gray-900">Convenient</p>
                  <p className="text-sm text-gray-600">Harrogate area</p>
                </div>
              </div>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                onClick={() => scrollToSection('contact')}
                className="bg-[#2d5f3f] hover:bg-[#1e4029] text-white px-8 py-6 text-lg transition-all duration-300 hover:scale-105 hover:shadow-lg group w-full sm:w-[200px] h-[60px]"
              >
                Enquire Now
                <ArrowRight className="ml-2 w-5 h-5 transition-transform duration-300 group-hover:translate-x-1" />
              </Button>
              <Button
                onClick={() => scrollToSection('pricing')}
                variant="outline"
                className="border-2 border-[#2d5f3f] text-[#2d5f3f] hover:bg-[#2d5f3f] hover:text-white px-8 py-6 text-lg transition-all duration-300 hover:scale-105 w-full sm:w-[200px] h-[60px]"
              >
                View Pricing
              </Button>
            </div>

            {/* Contact Info */}
            <div className="flex flex-col sm:flex-row gap-6 pt-4 border-t border-gray-200">
              <a
                href="tel:07788754165"
                className="flex items-center space-x-3 text-gray-700 hover:text-[#2d5f3f] transition-colors duration-200 group">

                <div className="w-10 h-10 rounded-full bg-[#2d5f3f]/10 flex items-center justify-center group-hover:bg-[#2d5f3f] transition-colors duration-300">
                  <Phone className="w-5 h-5 text-[#2d5f3f] group-hover:text-white transition-colors duration-300" />
                </div>
                <div>
                  <p className="text-xs text-gray-500">Call or WhatsApp</p>
                  <p className="font-semibold">07788 754165</p>
                </div>
              </a>
              <a
                href="mailto:enquiries@equi-go.co.uk"
                className="flex items-center space-x-3 text-gray-700 hover:text-[#2d5f3f] transition-colors duration-200 group">

                <div className="w-10 h-10 rounded-full bg-[#2d5f3f]/10 flex items-center justify-center group-hover:bg-[#2d5f3f] transition-colors duration-300">
                  <Mail className="w-5 h-5 text-[#2d5f3f] group-hover:text-white transition-colors duration-300" />
                </div>
                <div>
                  <p className="text-xs text-gray-500">Email us</p>
                  <p className="font-semibold">enquiries@equi-go.co.uk</p>
                </div>
              </a>
            </div>
          </div>

          {/* Right Image */}
          <div className="relative animate-fadeInUp" style={{ animationDelay: '0.2s' }}>
            <div className="relative rounded-2xl overflow-hidden shadow-2xl transform hover:scale-105 transition-transform duration-500">
              <img
                src="https://static.wixstatic.com/media/5924bc_148da214afbf4252918ecb719a65d375~mv2.jpg/v1/fill/w_800,h_521,al_c,q_85,usm_0.66_1.00_0.01,enc_avif,quality_auto/WhatsApp%20Image%202025-12-13%20at%2014_03_edited.jpg"
                alt="Equi-Go Horsebox"
                className="w-full h-auto" />
            </div>
            {/* Decorative elements */}
            <div className="absolute -z-10 top-8 -right-8 w-72 h-72 bg-[#c8a882] rounded-full opacity-20 blur-3xl"></div>
            <div className="absolute -z-10 -bottom-8 -left-8 w-72 h-72 bg-[#2d5f3f] rounded-full opacity-10 blur-3xl"></div>
          </div>
        </div>
      </div>
    </section>);

};

export default Hero;